/*
 * The takeover wrapper (D-106). Collapsed, it is one more link of the fill-height chain
 * (documentation/engineering/fill-height-tables.md) and adds nothing to the page but the
 * expander; expanded, it becomes a fixed layer over the viewport carrying a thin bar above
 * the same block, which never moves in the DOM.
 */
:host {
  /*
   * Room the surfaces leave at the right of their sentence line so the expander never sits on
   * the words. Declared here so the number lives in one place, and consumed by each surface's
   * own `agwa-filter-summary` rule - the wrapper must not reach into a surface's internals,
   * and a custom property inherits through the flat tree into the slotted block.
   */
  --agwa-fullscreen-control-inset: 2rem;

  /*
   * WHERE the expander sits inside that room. Published rather than fixed because the row the
   * control rides differs per surface: a list's one-line filter sentence wants the icon at its
   * top edge, a tabbed page's horizontal strip is a taller row and wants it centred on the tabs,
   * and a SIDE-tabbed page has no top strip at all, so the control rides the top of the rail
   * instead - above the first tab (#373, architect 2026-09-19: "why not make vertical tabs have
   * the full screen above the first tab").
   *
   * `left` exists because that last case cannot be said with `right` alone: the rail is on the
   * other side of the block, and its width is the tab labels'. A surface that wants it there
   * sets `right: auto` and gives `left` a value. The three defaults are the list surfaces'
   * existing placement, so a surface that says nothing is unchanged.
   */
  --agwa-fullscreen-control-top: 0.25rem;
  --agwa-fullscreen-control-right: 0.25rem;
  --agwa-fullscreen-control-left: auto;

  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

/*
 * The takeover. Fixed rather than a dialog because nothing may move in the DOM: the block
 * keeps its subscriptions, its ticks, its scroll and its own fill-height chain, and only the
 * host's positioning changes.
 *
 * LAYER: above the app masthead (`--layer-header: 150`, components/header-styles.css) and
 * below every Shoelace overlay (`--sl-z-index-drawer` 700, `--sl-z-index-dialog` 800,
 * `--sl-z-index-toast` 950), so a dialog or a toast raised from inside the takeover still
 * lands on top of it.
 *
 * GROUND: `--agwa-surface-raised` is what global.css actually paints on html/body, so the
 * takeover reads as the same page rather than a panel over it.
 */
:host([expanded]) {
  position: fixed;
  inset: 0;
  z-index: 400;
  box-sizing: border-box;
  padding: 0.25rem 0.75rem 0;
  background: var(--agwa-surface-raised);
}

/*
 * The bar exists only while taken over: with the app header covered, this is the only thing
 * left saying which page is on screen, and it carries the way back out.
 */
.bar {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid var(--agwa-divider);
}

.bar-title {
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--agwa-text-subtle);
}

/* The way out sits at the far end of the bar, where the expander it replaced sat. */
.exit {
  margin-left: auto;
}

/*
 * The block's own area, and the positioning context for the expander. It is a flex column
 * that may shrink so the slotted block inherits exactly the chain it had before the wrapper
 * existed - collapsed it is the page's remaining height, expanded it is the viewport's.
 */
.content {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

/*
 * The expander and the exit are plain icon buttons in the quiet palette the rail's own fold
 * control uses (agwa-filter-rail.styles.css): a Shoelace button here would out-weigh the
 * sentence it sits beside.
 */
.expand,
.exit {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.15rem;
  font: inherit;
  color: var(--agwa-text-subtle);
  background: none;
  border: 0;
  border-radius: 3px;
  cursor: pointer;
}

.expand:hover,
.exit:hover {
  color: var(--agwa-text);
  background: var(--agwa-surface-hover);
}

.expand:focus-visible,
.exit:focus-visible {
  outline: 2px solid var(--agwa-accent);
  outline-offset: -2px;
}

.expand sl-icon,
.exit sl-icon {
  font-size: 0.875rem;
}

/*
 * The expander rides at the top-right of the block, which is the sentence line's row and the
 * opposite corner from the rail's fold control. Absolutely positioned so it costs the block
 * no layout and the wrapper never has to know how the surface inside it is built. The two
 * offsets come from the properties declared on `:host` above, whose defaults match the sentence
 * strip's own padding, so the icon centres on the FIRST line of the sentence and stays there
 * when a long query wraps to a second; a tabbed page moves it down onto its taller strip.
 */
.expand {
  position: absolute;
  top: var(--agwa-fullscreen-control-top);
  right: var(--agwa-fullscreen-control-right);
  left: var(--agwa-fullscreen-control-left);
  z-index: 1;
}

/*
 * While taken over, the bar's exit IS the expander's replacement, so the two are never on
 * screen together. Hidden rather than unrendered so the control the focus returns to is the
 * same element throughout.
 */
:host([expanded]) .expand {
  display: none;
}
